import { type Common } from './common.js';
import type * as Address from './models/address.js';
import { type OffsetPaginationResponse } from './models/common.js';
import type * as Pharmacy from './models/pharmacy.js';
import { type ImportValidation } from './models/provider.js';

/**
 * Shared args for `findVerifiedPharmacies`. The `q` filter is mutually
 * exclusive with `name` / `cities` — the backend rejects combinations
 * with a 400 because they would AND-collapse to an empty result set.
 * Encoded as `LegacyQArgs | NameAndCitiesArgs` below so callers
 * mistakenly mixing the two get a typecheck failure rather than a
 * runtime error.
 */
interface SharedFindArgs {
  page: number;
  limit: number;
  state?: string;
  googlePlaceId?: string;
  /**
   * Anchor for distance + nearest-first sort. When present, every result
   * row gets `distanceMiles` and results come back ordered closest-first
   * (PostGIS KNN). Rows whose address has no resolved location are
   * excluded. When omitted, no distance is returned and results are
   * ordered alphabetically by name (street as tiebreaker).
   *
   * **`limit` is required and capped at 50 when `nearTo` is set** — KNN
   * over the entire pharmacy table is wasteful, so narrow with `name` /
   * `cities` instead of bumping the limit.
   */
  nearTo?: Address.GeoPoint;
}

interface LegacyQArgs {
  /**
   * Tri-column ilike across `pharmacies.name`, `addresses.address`, and
   * `pharmacies.ncpdpId`. Used by legacy `PatientPharmacies` /
   * `PatientNCPDPPharmacies` primitives. New callers should prefer
   * `name` + `cities` for clearer separation.
   */
  q?: string;
  /** Mutually exclusive with `q` — backend returns 400 on combination. */
  name?: never;
  /** Mutually exclusive with `q` — backend returns 400 on combination. */
  cities?: never;
}

interface NameAndCitiesArgs {
  /** Mutually exclusive with `name` / `cities` — backend returns 400 on combination. */
  q?: never;
  /** Case-insensitive substring match on `pharmacies.name` only. */
  name?: string;
  /**
   * Composite (city, state) tuple match. OR-combined across the array.
   * **City matching is case-insensitive** — necessary because
   * NCPDP-imported pharmacy addresses are uppercase ('CUPERTINO') while
   * practice and other source addresses are mixed-case ('Cupertino').
   * State matching is exact (Address.State enum is uppercase 2-letter).
   * Empty array is treated as no filter.
   */
  cities?: { city: string; state: Address.State }[];
}

export type FindVerifiedPharmaciesArgs = SharedFindArgs & (LegacyQArgs | NameAndCitiesArgs);

export class API {
  common: Common;
  baseEndpoint = '/pharmacies';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(
    args: Pharmacy.FindAllOptions
  ): Promise<OffsetPaginationResponse<Pharmacy.PharmacyWithAddress>> {
    const url = this.common.constructPath(this.baseEndpoint, args);
    return await this.common.getJSON(url);
  }

  async import(
    pharmacies: Partial<Omit<Pharmacy.PrevisitPharmacy, 'id' | 'staticMapUrl'>>[]
  ): Promise<Pharmacy.Pharmacy[]> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.putJSON(url, { pharmacies });
  }

  async verifyImport(
    values: Record<string, string>[]
  ): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/import/verify`);
    return await this.common.postJSON(url, values);
  }

  async findVerifiedPharmacies(
    args: FindVerifiedPharmaciesArgs
  ): Promise<Pharmacy.PrevisitPharmacy[]> {
    const url = this.common.constructPath(`/patient-previsit/pharmacies`, args);
    return await this.common.getJSON(url);
  }

  /**
   * Distinct (city, state) tuples from NCPDP-tagged pharmacies.
   * Drives the city multi-select picker in the patient app.
   */
  async findPharmacyCities(
    args: {
      /**
       * Substring ilike on `addresses.city`. e.g. 'york' matches
       * 'New York', 'Yorktown', etc. Optional — omit to list all.
       */
      q?: string;
      /** Default 50 server-side, max 200. */
      limit?: number;
    } = {}
  ): Promise<Pharmacy.PharmacyCity[]> {
    const url = this.common.constructPath(`/patient-previsit/pharmacies/cities`, args);
    return await this.common.getJSON(url);
  }

  async finalizeImport(importStartedAt: Date): Promise<{ closedCount: number }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/finalize-import`);
    return await this.common.postJSON(url, { importStartedAt: importStartedAt.toISOString() });
  }
}
